import { useState, useRef, useCallback } from "react";

export const useMessageActions = (currentUser) => {
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const [reactionPosition, setReactionPosition] = useState({ x: 0, y: 0 });
    const [editingMessage, setEditingMessage] = useState(null);
    const [editMessageText, setEditMessageText] = useState("");
    const [replyingTo, setReplyingTo] = useState(null);
    const [swipeProgress, setSwipeProgress] = useState({});

    const reactionBoxRef = useRef(null);
    const longPressTimeout = useRef(null);
    const longPressTriggered = useRef(false);
    const touchStartPos = useRef({ x: 0, y: 0 });
    const touchStartX = useRef(null);
    const isReactingRef = useRef(false);
    const isScrollingRef = useRef(false);

    const openReactionBox = useCallback((messageId, x, y, message) => {
        setSelectedMessageId(messageId);
        setReactionPosition({ x, y });
        if (message?.senderId === currentUser?.uid) {
            setSelectionMode(true);
            setSelectedMessage(message);
        }
        longPressTriggered.current = true; // 👈 Add this
    }, [currentUser]);

    const closeReactionBox = useCallback(() => {
        setSelectedMessageId(null);
    }, []);

    const handleEditMessage = useCallback((message) => {
        setEditingMessage(message);
        setEditMessageText(message.text);
        setSelectedMessageId(null);
    }, []);

    const cancelEdit = useCallback(() => {
        setEditingMessage(null);
        setEditMessageText("");
    }, []);

    const handleReplyToMessage = useCallback((message, chatUser, users = {}) => {
        const senderName = message.senderId === currentUser.uid
            ? "You"
            : chatUser?.name || users[message.senderId]?.name || "Unknown";

        const replyData = {
            id: message.id,
            senderId: message.senderId,
            senderName,
            text: message.text,
            imageUrl: message.imageUrl,
            audioUrl: message.audioUrl
        };

        if (!message.text) {
            if (message.imageUrl) {
                replyData.displayText = "📷 Photo";
            } else if (message.audioUrl) {
                replyData.displayText = "🎤 Voice message";
            }
        }

        setReplyingTo(replyData);
    }, [currentUser]);

    const clearReply = useCallback(() => {
        setReplyingTo(null);
    }, []);

    const handleSwipeStart = useCallback((e, messageId) => {
        const touch = e.touches[0];
        touchStartPos.current = { x: touch.clientX, y: touch.clientY };
        touchStartX.current = touch.clientX;
        isScrollingRef.current = false;
        longPressTriggered.current = false;
    }, []);

    const handleSwipeMove = useCallback((e, messageId, isFromCurrentUser) => {
        const touch = e.touches[0];
        const dx = Math.abs(touch.clientX - touchStartPos.current.x);
        const dy = Math.abs(touch.clientY - touchStartPos.current.y);

        if (dx > 10) {
            e.preventDefault();
            isScrollingRef.current = false;
            clearTimeout(longPressTimeout.current);

            const deltaX = touch.clientX - touchStartX.current;

            if ((!isFromCurrentUser && deltaX > 0) || (isFromCurrentUser && deltaX < 0)) {
                const newProgress = Math.min(Math.max(deltaX, -80), 80);
                setSwipeProgress(prev => ({
                    ...prev,
                    [messageId]: newProgress
                }));
            }
        } else if (dy > 10) {
            isScrollingRef.current = true;
        }
    }, []);

    const handleSwipeEnd = useCallback((messageId, onReply) => {
        const progress = swipeProgress[messageId] || 0;

        if (Math.abs(progress) > 40) {
            onReply();
        }

        setSwipeProgress(prev => ({
            ...prev,
            [messageId]: 0
        }));

        touchStartX.current = null;
    }, [swipeProgress]);

    return {
        // States
        selectionMode,
        setSelectionMode,
        selectedMessage,
        setSelectedMessage,
        selectedMessageId,
        setSelectedMessageId,
        reactionPosition,
        editingMessage,
        editMessageText,
        setEditMessageText,
        replyingTo,
        swipeProgress,
        reactionBoxRef,
        longPressTimeout,
        longPressTriggered,
        isReactingRef,
        isScrollingRef,
        touchStartPos,
        touchStartX,

        // Actions
        openReactionBox,
        closeReactionBox,
        handleEditMessage,
        cancelEdit,
        handleReplyToMessage,
        clearReply,
        handleSwipeStart,
        handleSwipeMove,
        handleSwipeEnd,
    };
};